import { lazy } from 'react';
import { Resource, Action } from '@/constants/constants';

export const preloadExerciseEditorPage = () => import('@/pages/exercise-editor');
export const preloadMovexLibraryPage = () => import('@/pages/movex-library');
// export const preloadOtpPage = () => import('@/pages/otp-page');
// export const preloadRequestOtpPage = () => import('@/pages/request-otp');
export const preloadPatientsPage = () => import('@/pages/patients');
export const preloadExerciseLibraryPage = () => import('@/pages/exercise-library');
export const preloadPatientDetailsPage = () => import('@/pages/patients/patient');
export const preloadHelpPage = () => import('@/pages/help');
export const preloadPatientLoginPage = () => import('@/pages/login');
export const preloadMagicLinkPage = () => import('@/pages/magic-link');
export const preloadHomePage = () => import('@/pages/home');
export const preloadPostPerformancePage = () => import('@/pages/post-performance');
export const preloadNotFoundPage = () => import('@/pages/not-found');
export const preloadDashboardTagsPage = () => import('@/pages/tags');
export const preloadDashboardTherapistPage = () => import('@/pages/dashboard-physiotherapists');
export const preloadLinkExpiredPage = () => import('@/pages/link-expired');
export const preloadLobbyPage = () => import('@/pages/lobby');
export const preloadClinicSetupPage = () => import('@/pages/clinic-setup');
export const preloadTherapistOnboardedPage = () => import('@/pages/therapist-onboarded');
export const preloadClinicsAdminsPage = () => import('@/pages/clinics-admins');
export const preloadAppointmentsPage = () => import('@/pages/appointment');
export const preloadStaffPage = () => import('@/pages/staff');
export const preloadTermsPage = () => import('@/pages/terms');
export const preloadStaffProfilePage = () => import('@/pages/staff/staffProfile');
export const preloadProfilePage = () => import('@/pages/profile');
export const preloadDeactivateTherapistPage = () => import('@/pages/staff/deactivateTherapist');

const ExerciseEditor = lazy(preloadExerciseEditorPage);
const MovexLibrary = lazy(preloadMovexLibraryPage);
// const OtpPage = lazy(preloadOtpPage);
// const RequestOtp = lazy(preloadRequestOtpPage);
const Patients = lazy(preloadPatientsPage);
const ExerciseLibrary = lazy(preloadExerciseLibraryPage);
const Patient = lazy(preloadPatientDetailsPage);
const Help = lazy(preloadHelpPage);
const PatientLogin = lazy(preloadPatientLoginPage);
const MagicLinkPage = lazy(preloadMagicLinkPage);
const Home = lazy(preloadHomePage);
const PostPerformance = lazy(preloadPostPerformancePage);
const NotFoundPage = lazy(preloadNotFoundPage);
const DashboardTags = lazy(preloadDashboardTagsPage);
const DashboardTherapist = lazy(preloadDashboardTherapistPage);
const LinkExpired = lazy(preloadLinkExpiredPage);
const Lobby = lazy(preloadLobbyPage);
const ClinicSetup = lazy(preloadClinicSetupPage);
const TherapistOnboarded = lazy(preloadTherapistOnboardedPage);
const ClinicsAdmins = lazy(preloadClinicsAdminsPage);
const Appointments = lazy(preloadAppointmentsPage);
const Staff = lazy(preloadStaffPage);
const StaffProfile = lazy(preloadStaffProfilePage);
const Profile = lazy(preloadProfilePage);
const DeactivateTherapist = lazy(preloadDeactivateTherapistPage);
const Terms = lazy(preloadTermsPage);

const useRoutes = () => {
  const privateRoute = [
    {
      route: '/exercise-library',
      element: <ExerciseLibrary />,
      requiredPermission: { subject: Resource.EXERCISE_LIBRARY, action: Action.VIEW },
    },
    {
      route: '/patients',
      element: <Patients />,
      requiredPermission: { subject: Resource.PATIENT, action: Action.VIEW },
    },
    {
      route: '/patients/:id',
      element: <Patient />,
      requiredPermission: { subject: Resource.PATIENT, action: Action.VIEW },
    },
    {
      route: '/help',
      element: <Help />,
    },
    {
      route: '/terms',
      element: <Terms />,
    },
    {
      route: '/patients/home',
      element: <Home />,
      requiredPermission: { subject: Resource.SESSION, action: Action.VIEW },
    },
    {
      route: '/patients/home/post-performance',
      element: <PostPerformance />,
      requiredPermission: { subject: Resource.SESSION, action: Action.VIEW },
    },
    {
      route: '/dashboard/tags',
      element: <DashboardTags />,
      requiredPermission: { subject: Resource.TAG, action: Action.VIEW },
    },
    {
      route: '/dashboard/physiotherapists',
      element: <DashboardTherapist />,
      requiredPermission: { subject: Resource.CLINIC, action: Action.VIEW },
    },
    {
      route: '/dashboard/exercise-studio',
      element: <MovexLibrary />,
      requiredPermission: { subject: Resource.EXERCISE, action: Action.VIEW },
    },
    {
      route: '/dashboard/exercise-editor',
      element: <ExerciseEditor />,
      requiredPermission: { subject: Resource.EXERCISE, action: Action.CREATE },
    },
    {
      route: '/dashboard/clinics-admins',
      element: <ClinicsAdmins />,
      requiredPermission: { subject: Resource.CLINIC, action: Action.VIEW },
    },
    {
      route: '/appointments',
      element: <Appointments />,
      requiredPermission: { subject: Resource.APPOINTMENT, action: Action.VIEW },
    },
    {
      route: '/user-management',
      element: <Staff />,
      requiredPermission: { subject: Resource.STAFF, action: Action.VIEW },
    },
    {
      route: '/user-management/:id',
      element: <StaffProfile />,
      requiredPermission: { subject: Resource.STAFF, action: Action.VIEW },
    },
    {
      route: '/user-management/:id/deactivate-therapist',
      element: <DeactivateTherapist />,
      requiredPermission: { subject: Resource.STAFF, action: Action.UPDATE },
    },
    {
      // Own profile - unlike /user-management, not gated behind the
      // STAFF permission since every logged-in staff member must
      // always be able to reach their own profile.
      route: '/profile',
      element: <Profile />,
    },
  ];

  const puplicRoute = [
    { route: '/lobby', element: <Lobby /> },
    { route: '/login', element: <PatientLogin /> },
    // { route: '/request-otp', element: <RequestOtp /> },
    // { route: '/otp-page', element: <OtpPage /> },
  ];

  // Requires an authenticated session (unlike puplicRoute) but renders full-screen,
  // outside LayoutSection's dashboard chrome (unlike privateRoute) - both onboarding
  // gates read GET /onboarding/status themselves and redirect onward once there's
  // no active flow (Clinic Owner -> /appointments, Therapist -> /exercise-library),
  // so neither can sit behind the dashboard layout.
  const protectedStandaloneRoute = [
    { route: '/clinic-setup', element: <ClinicSetup /> },
    { route: '/therapist-onboarded', element: <TherapistOnboarded /> },
  ];

  const PermissionDeniedPage = lazy(() => import('@/pages/permission-denied'));

  const sherdRoute = [
    { route: '/auth/magic-link', element: <MagicLinkPage /> },
    { route: '/permission-denied', element: <PermissionDeniedPage /> },
    { route: '/link-expired', element: <LinkExpired /> },
    { route: '*', element: <NotFoundPage /> },
  ];

  return { privateRoute, puplicRoute, sherdRoute, protectedStandaloneRoute };
};

export default useRoutes;
